import React, { useState } from "react";
import Box from "@material-ui/core/Box";
import Paper from "@material-ui/core/Paper";
import Typography from "@material-ui/core/Typography";
import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import Table from "@material-ui/core/Table";
import TableHead from "@material-ui/core/TableHead";
import TableBody from "@material-ui/core/TableBody";
import TableRow from "@material-ui/core/TableRow";
import TableCell from "@material-ui/core/TableCell";
import { makeStyles } from "@material-ui/core/styles";
import { P, VALET } from "./constants";
import { ini } from "./helpers";
import { Card } from "./ui";

const useStyles = makeStyles({
  root: { display: "flex", flexDirection: "column", gap: 12 },

  topGrid: { display: "grid", gridTemplateColumns: "1fr auto", gap: 12, alignItems: "start" },
  viewToggle: { display: "flex", gap: 2, background: "var(--color-background-secondary)", borderRadius: 7, padding: 3, border: "0.5px solid var(--color-border-tertiary)", alignSelf: "flex-start" },
  // Default MUI Button size; brand/active colors stay inline. Lowercase casing kept off.
  toggleBtn: { textTransform: "none", minWidth: 0, padding: "5px 16px", borderRadius: 5 },

  reportPanel: { background: "var(--color-background-primary)", border: "0.5px solid var(--color-border-tertiary)", borderRadius: 10, padding: "10px 14px", display: "flex", alignItems: "center", gap: 14, minWidth: 0 },
  reportStatusCol: { flexShrink: 0 },
  reportLabel: { fontWeight: 600, color: "var(--color-text-secondary)", letterSpacing: ".04em", marginBottom: 2 },
  reportStatusRow: { display: "flex", alignItems: "center", gap: 6 },
  reportDot: { width: 8, height: 8, borderRadius: "50%" },
  reportInputsRow: { display: "flex", alignItems: "center", gap: 8, flex: 1 },
  reportFieldLabel: { color: "var(--color-text-tertiary)", marginBottom: 2 },
  reportEmailField: { width: 200 },
  reportInput: { background: "transparent" },
  reportBtnRow: { display: "flex", gap: 6, flexShrink: 0 },
  reportBtn: { textTransform: "none", minWidth: 0, padding: "5px 10px", borderRadius: 5, fontWeight: 600 },

  fdCol: { display: "flex", flexDirection: "column", gap: 12 },

  kpiGrid: { display: "grid", gridTemplateColumns: "repeat(5,minmax(0,1fr))", gap: 8 },
  kpiTile: { background: "var(--color-background-primary)", border: "0.5px solid var(--color-border-tertiary)", borderRadius: 10, padding: "12px 14px" },
  kpiLabel: { color: "var(--color-text-secondary)", marginBottom: 4 },
  kpiValue: { fontWeight: 800, fontFamily: "var(--font-mono)" },
  kpiSub: { color: "var(--color-text-tertiary)", marginTop: 2 },

  chartRow: { display: "flex", gap: 4, alignItems: "flex-end", height: 80, padding: "0 4px" },
  chartCol: { flex: 1, display: "flex", flexDirection: "column", alignItems: "center", gap: 3 },
  chartValue: { fontFamily: "var(--font-mono)" },
  chartBar: { width: "100%", borderRadius: "3px 3px 0 0", minHeight: 4, transition: "height .2s" },
  chartSummary: { display: "flex", justifyContent: "space-between", marginTop: 8, padding: "6px 8px", background: "var(--color-background-secondary)", borderRadius: 6, border: "0.5px solid var(--color-border-tertiary)" },
  summaryItem: { color: "var(--color-text-secondary)" },
  summaryStrongNv: { color: P.nv },
  summaryStrongPd: { color: "#241C8A" },
  summaryStrongBl: { color: "#3127B8" },

  threeCol: { display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 },

  searchField: { width: "100%", marginBottom: 8 },

  listCol: { display: "flex", flexDirection: "column", gap: 2 },
  listColScroll: { display: "flex", flexDirection: "column", gap: 2, maxHeight: 320, overflowY: "auto" },

  inParkRow: { display: "flex", justifyContent: "space-between", alignItems: "center", padding: "7px 8px", borderRadius: 5, border: "0.5px solid var(--color-border-tertiary)" },
  rowHeadLine: { display: "flex", alignItems: "center", gap: 6 },
  plateGreen: { fontFamily: "var(--font-mono)", fontWeight: 700, color: "#2E7D32", background: "#E6F4E8", padding: "1px 6px", borderRadius: 3 },
  guestName: { fontWeight: 500, color: "var(--color-text-primary)" },
  rmText: { color: "var(--color-text-tertiary)" },
  metaRow: { display: "flex", gap: 8, marginTop: 3 },
  metaText: { color: "var(--color-text-tertiary)" },
  noteText: { color: "#241C8A" },
  inParkRight: { textAlign: "right", flexShrink: 0, marginLeft: 8 },
  outText: { fontWeight: 600, color: P.nv },
  unpaidBadge: { fontWeight: 700, color: "#2E7D32", background: "#E6F4E8", padding: "1px 5px", borderRadius: 3 },

  arrRow: { display: "flex", justifyContent: "space-between", alignItems: "center", padding: "8px 10px", borderRadius: 6, border: "0.5px solid var(--color-border-tertiary)", background: "var(--color-background-primary)" },
  etaLine: { display: "flex", alignItems: "center", gap: 6, marginBottom: 3 },
  etaText: { fontFamily: "var(--font-mono)", fontWeight: 800, color: P.nv },
  nameInline: { fontWeight: 500 },
  pillsRow: { display: "flex", gap: 7, flexWrap: "wrap" },
  plateTintGreen: { fontFamily: "var(--font-mono)", background: "#E6F4E8", padding: "1px 5px", borderRadius: 3, color: "#2E7D32" },
  secondaryText: { color: "var(--color-text-secondary)" },
  upsellsRow: { display: "flex", gap: 4, marginTop: 3, flexWrap: "wrap" },
  upsellChip: { fontWeight: 600, padding: "2px 6px", borderRadius: 4, background: "#E6F4E8", color: "#2E7D32", border: "0.5px solid #BFE3C6" },
  paidLabel: { fontWeight: 600, flexShrink: 0 },

  depRow: { display: "flex", justifyContent: "space-between", alignItems: "center", padding: "8px 10px", borderRadius: 6 },
  coText: { fontFamily: "var(--font-mono)", fontWeight: 800 },
  plateTintGray: { fontFamily: "var(--font-mono)", background: "var(--color-background-secondary)", padding: "1px 5px", borderRadius: 3, color: "var(--color-text-secondary)" },
  freedBox: { marginTop: 10, padding: "8px 10px", background: "#E6F4E8", borderRadius: 6, border: "0.5px solid #BFE3C6" },
  freedRow: { display: "flex", justifyContent: "space-between" },
  freedLabel: { color: "#2E7D32" },
  freedVal: { fontWeight: 700, color: "#2E7D32", fontFamily: "var(--font-mono)" },

  // Status badge base; conditional bg/color from stBadge map stay inline.
  stBadge: { fontWeight: 600, padding: "2px 6px", borderRadius: 3, display: "inline-block" },

  // Valet & Concierge table — compact override of MUI defaults.
  table: { width: "100%" },
  th: { fontWeight: 600, color: "var(--color-text-secondary)", textAlign: "left", padding: "4px 8px", borderBottom: "0.5px solid var(--color-border-tertiary)", letterSpacing: ".04em", whiteSpace: "nowrap" },
  td: { padding: "8px 8px", borderBottom: "none" },
  tdName: { padding: "8px 8px", borderBottom: "none", fontWeight: 500 },
  tdMono: { padding: "8px 8px", borderBottom: "none", fontFamily: "var(--font-mono)" },
  tdSecondary: { padding: "8px 8px", borderBottom: "none", color: "var(--color-text-secondary)" },
  tdTertiary: { padding: "8px 8px", borderBottom: "none", color: "var(--color-text-tertiary)" },
  tr: { borderBottom: "0.5px solid var(--color-border-tertiary)" },
  plateTd: { fontFamily: "var(--font-mono)", background: "var(--color-background-secondary)", padding: "1px 5px", borderRadius: 3 },
  assignedCell: { display: "flex", alignItems: "center", gap: 5 },
  initialsDot: { width: 18, height: 18, borderRadius: "50%", background: "#E6F4E8", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 700, color: "#2E7D32" },
});

export default function ArrivalsTab() {
  const cls = useStyles();
  const [view, setView] = useState("fd");
  const [search, setSearch] = useState("");
  const [reportEmail, setReportEmail] = useState("dm@grandhyatt.com.au");
  const [reportTime, setReportTime] = useState("07:00");
  const [reportActive, setReportActive] = useState(true);
  const [reportSent, setReportSent] = useState(false);

  const IN_PARK = [
    {n:"Thomas Wren",       rm:"509", pl:"POR 999", bay:"B-04", since:"08:45", checkout:"12:30", typ:"Valet", paid:true},
    {n:"Pinnacle Corp",     rm:"Conf A",pl:"--",    bay:"Multi", since:"09:00", checkout:"17:00", typ:"Self",  paid:true,  note:"12 vehicles"},
    {n:"Caroline White",   rm:"314", pl:"AUD 887", bay:"B-12", since:"09:30", checkout:"10:00", typ:"Valet", paid:true},
    {n:"Zoe Mackenzie",    rm:"107", pl:"RAN 654", bay:"B-03", since:"10:15", checkout:"10:30", typ:"Valet", paid:true},
    {n:"Alex Drummond",    rm:"501", pl:"FOR 234", bay:"A-07", since:"07:30", checkout:"11:00", typ:"Self",  paid:true},
    {n:"Harry Chen",       rm:"222", pl:"MAZ 901", bay:"A-22", since:"10:00", checkout:"12:00", typ:"Self",  paid:true},
    {n:"Priya Sharma",     rm:"418", pl:"LEX 337", bay:"B-09", since:"11:30", checkout:"14:00", typ:"Valet", paid:true},
    {n:"Dr. Marcus Webb",  rm:"331", pl:"WEB 441", bay:"C-14", since:"08:00", checkout:"15:00", typ:"Self",  paid:true},
    {n:"Overnight Guest",  rm:"255", pl:"NGT 221", bay:"A-18", since:"yesterday",checkout:"16:00",typ:"Self",paid:true},
    {n:"Sarah Okafor",     rm:"602", pl:"OKA 887", bay:"C-01", since:"09:45", checkout:"13:00", typ:"Self",  paid:false, note:"Unpaid"},
  ];

  const ARRIVING = [
    {n:"James Harrington", rm:"412", eta:"14:00", pl:"ABC 123", typ:"Valet", paid:true,  note:"VIP -- Platinum member", upsells:["Car Wash","EV Charging"]},
    {n:"Mei Lin",          rm:"205", eta:"14:30", pl:"XYZ 789", typ:"Self",  paid:true, upsells:["EV Charging"]},
    {n:"Robert & S. Flint",rm:"Suite 1",eta:"15:00",pl:"MER 001",typ:"Valet",paid:false, note:"Anniversary", upsells:["Car Wash","Detail & Polish","Valet Upgrade"]},
    {n:"Dr. Anika Patel",  rm:"318", eta:"16:00", pl:"HON 456", typ:"Self",  paid:true, upsells:[]},
    {n:"Nguyen Family",    rm:"220", eta:"17:00", pl:"TYO 312", typ:"Self",  paid:false, upsells:["Car Wash"]},
  ];

  const DEPARTING = [
    {n:"Caroline White",   rm:"314", co:"10:00", pl:"AUD 887", bay:"B-12", typ:"Valet", st:"ready",     paid:true, upsells:["Car Wash","EV Charging"]},
    {n:"Zoe Mackenzie",    rm:"107", co:"10:30", pl:"RAN 654", bay:"B-03", typ:"Valet", st:"retrieving", paid:true, upsells:["Detail & Polish"]},
    {n:"Alex Drummond",    rm:"501", co:"11:00", pl:"FOR 234", bay:"A-07", typ:"Self",  st:"departed",   paid:true, upsells:[]},
    {n:"Harry Chen",       rm:"222", co:"12:00", pl:"MAZ 901", bay:"A-22", typ:"Self",  st:"expected",   paid:true, upsells:["Car Wash"]},
    {n:"Priya Sharma",     rm:"418", co:"14:00", pl:"LEX 337", bay:"B-09", typ:"Valet", st:"expected",   paid:true,  note:"Late checkout", upsells:["Valet Upgrade","EV Charging"]},
  ];

  const TOTAL_BAYS = 100;
  const currentOcc = IN_PARK.length;
  const arrivingCount = ARRIVING.length;
  const departingCount = DEPARTING.length;
  const projectedPeak = currentOcc + arrivingCount - DEPARTING.filter(d=>d.st==="expected"||d.st==="ready").length;
  const available = TOTAL_BAYS - currentOcc;

  const HOURLY = [
    {h:"08:00",occ:52},{h:"09:00",occ:61},{h:"10:00",occ:58},{h:"11:00",occ:55},
    {h:"12:00",occ:53},{h:"13:00",occ:62},{h:"14:00",occ:70},{h:"15:00",occ:74},
    {h:"16:00",occ:76},{h:"17:00",occ:71},{h:"18:00",occ:65},{h:"19:00",occ:58},
  ];
  const maxOcc = Math.max(...HOURLY.map(h=>h.occ));

  const stBadge = st => {
    const map = {expected:{bg:"#E6F4E8",c:"#2E7D32"},arrived:{bg:"#E6F4E8",c:"#2E7D32"},ready:{bg:"#E6F4E8",c:"#2E7D32"},retrieving:{bg:"#E6F4E8",c:"#2E7D32"},departed:{bg:"var(--color-background-secondary)",c:"var(--color-text-tertiary)"},parked:{bg:"#E6F4E8",c:"#2E7D32"}};
    const s=map[st]||{bg:"var(--color-background-secondary)",c:"var(--color-text-secondary)"};
    return <Typography variant="caption" component="span" className={cls.stBadge} style={{background:s.bg,color:s.c}}>{st.toUpperCase()}</Typography>;
  };

  return (
    <Box className={cls.root}>

      <Box className={cls.topGrid}>
        <Box className={cls.viewToggle}>
          {[["fd","Front Desk"],["valet","Valet & Concierge"]].map(([id,label])=>(
            <Button key={id} size="small" onClick={()=>setView(id)} className={cls.toggleBtn} style={{fontWeight:view===id?600:400,color:view===id?"#fff":"var(--color-text-secondary)",background:view===id?P.nv:"transparent"}}>{label}</Button>
          ))}
        </Box>
        <Paper elevation={0} className={cls.reportPanel}>
          <Box className={cls.reportStatusCol}>
            <Typography variant="caption" component="div" className={cls.reportLabel}>DAILY REPORT</Typography>
            <Box className={cls.reportStatusRow}>
              <span className={cls.reportDot} style={{background:reportActive?"#241C8A":"var(--color-border-secondary)"}} />
              <Typography variant="body2" component="span" style={{color:reportActive?"#241C8A":"var(--color-text-tertiary)",fontWeight:reportActive?600:400}}>{reportActive?"Active":"Paused"}</Typography>
            </Box>
          </Box>
          <Box className={cls.reportInputsRow}>
            <div>
              <Typography variant="caption" component="div" className={cls.reportFieldLabel}>Send to</Typography>
              <TextField variant="outlined" size="small" value={reportEmail} onChange={e=>setReportEmail(e.target.value)} className={cls.reportEmailField} InputProps={{className:cls.reportInput}} />
            </div>
            <div>
              <Typography variant="caption" component="div" className={cls.reportFieldLabel}>Send daily at</Typography>
              <TextField variant="outlined" size="small" type="time" value={reportTime} onChange={e=>setReportTime(e.target.value)} InputProps={{className:cls.reportInput}} />
            </div>
          </Box>
          <Box className={cls.reportBtnRow}>
            <Button size="small" onClick={()=>setReportActive(!reportActive)} className={cls.reportBtn} style={{border:"0.5px solid "+(reportActive?"#3EAC4D":P.nv),background:reportActive?"#E6F4E8":P.nv,color:reportActive?"#2E7D32":"#fff"}}>{reportActive?"Pause":"Activate"}</Button>
            <Button size="small" onClick={()=>{setReportSent(true);setTimeout(()=>setReportSent(false),3000);}} className={cls.reportBtn} style={{background:reportSent?"#E6F4E8":P.nv,color:reportSent?"#2E7D32":"#fff"}}>{reportSent?"Sent!":"Send Now"}</Button>
          </Box>
        </Paper>
      </Box>

      {view==="fd" && (
        <Box className={cls.fdCol}>

          {/* KPI row */}
          <Box className={cls.kpiGrid}>
            {[
              ["Currently In Park", currentOcc, P.nv,       "of "+TOTAL_BAYS+" bays"],
              ["Available Bays",    available,   "#241C8A",  "right now"],
              ["Arriving Today",    arrivingCount,"#3127B8", "expected"],
              ["Departing Today",   departingCount,P.nv,     "scheduled"],
              ["Projected Peak",    projectedPeak, projectedPeak>80?"#241C8A":P.nv, "bays at peak"],
            ].map(([l,v,c,s])=>(
              <Paper key={l} elevation={0} className={cls.kpiTile}>
                <Typography variant="caption" component="div" className={cls.kpiLabel}>{l}</Typography>
                <Typography variant="h5" component="div" className={cls.kpiValue} style={{color:c}}>{v}</Typography>
                <Typography variant="caption" component="div" className={cls.kpiSub}>{s}</Typography>
              </Paper>
            ))}
          </Box>

          {/* Inventory timeline */}
          <Card title="Bay Occupancy Today" subtitle="Hourly occupancy across all bays -- peak expected 16:00">
            <Box className={cls.chartRow}>
              {HOURLY.map((h,i)=>{
                const isNow = h.h==="14:00";
                const height = Math.round(h.occ/maxOcc*100);
                return (
                  <Box key={i} className={cls.chartCol}>
                    <Typography variant="caption" component="div" className={cls.chartValue} style={{fontWeight:isNow?700:400,color:isNow?P.nv:"var(--color-text-tertiary)"}}>{h.occ}</Typography>
                    <div className={cls.chartBar} style={{background:isNow?P.nv:h.occ>75?"#3127B8":h.occ>60?"#3127B8":"#3EAC4D",height:height+"%"}} />
                    <Typography variant="caption" component="div" style={{color:isNow?P.nv:"var(--color-text-tertiary)",fontWeight:isNow?700:400}}>{h.h.slice(0,5)}</Typography>
                  </Box>
                );
              })}
            </Box>
            <Box className={cls.chartSummary}>
              <Typography variant="caption" component="span" className={cls.summaryItem}>Total bays: <strong className={cls.summaryStrongNv}>{TOTAL_BAYS}</strong></Typography>
              <Typography variant="caption" component="span" className={cls.summaryItem}>Current: <strong className={cls.summaryStrongNv}>{currentOcc} occupied</strong></Typography>
              <Typography variant="caption" component="span" className={cls.summaryItem}>Available: <strong className={cls.summaryStrongPd}>{available} free</strong></Typography>
              <Typography variant="caption" component="span" className={cls.summaryItem}>Arriving next 4h: <strong className={cls.summaryStrongNv}>{arrivingCount}</strong></Typography>
              <Typography variant="caption" component="span" className={cls.summaryItem}>Departing next 4h: <strong className={cls.summaryStrongBl}>{DEPARTING.filter(d=>d.st!=="departed").length}</strong></Typography>
            </Box>
          </Card>

          <Box className={cls.threeCol}>

            {/* Currently in park */}
            <Card title="Currently In Park" subtitle={currentOcc+" vehicles with active bookings"}>
              <TextField variant="outlined" size="small" value={search} onChange={e=>setSearch(e.target.value)} placeholder="Search guest, plate, room..." className={cls.searchField} InputProps={{className:cls.reportInput}} />
              <Box className={cls.listColScroll}>
                {IN_PARK.filter(a=>!search||(a.n+a.rm+a.pl).toLowerCase().includes(search.toLowerCase())).map((a,i)=>(
                  <Box key={i} className={cls.inParkRow} style={{background:a.paid?a.note?"#E6F4E808":"var(--color-background-primary)":"#E6F4E80A"}}>
                    <div>
                      <Box className={cls.rowHeadLine}>
                        <Typography variant="body2" component="span" className={cls.plateGreen}>{a.pl}</Typography>
                        <Typography variant="body2" component="span" className={cls.guestName}>{a.n}</Typography>
                        <Typography variant="caption" component="span" className={cls.rmText}>Rm {a.rm}</Typography>
                      </Box>
                      <Box className={cls.metaRow}>
                        <Typography variant="caption" component="span" className={cls.metaText}>Bay {a.bay}</Typography>
                        <Typography variant="caption" component="span" className={cls.metaText}>Since {a.since}</Typography>
                        {a.note&&<Typography variant="caption" component="span" className={cls.noteText}>{a.note}</Typography>}
                      </Box>
                    </div>
                    <Box className={cls.inParkRight}>
                      <Typography variant="body2" component="div" className={cls.outText}>Out {a.checkout}</Typography>
                      {!a.paid&&<Typography variant="caption" component="span" className={cls.unpaidBadge}>UNPAID</Typography>}
                    </Box>
                  </Box>
                ))}
              </Box>
            </Card>

            {/* Arriving */}
            <Card title="Arriving Today" subtitle={arrivingCount+" guests expected"}>
              <Box className={cls.listCol}>
                {ARRIVING.map((a,i)=>(
                  <Box key={i} className={cls.arrRow}>
                    <div>
                      <Box className={cls.etaLine}>
                        <Typography variant="body2" component="span" className={cls.etaText}>{a.eta}</Typography>
                        <Typography variant="body2" component="span" className={cls.nameInline}>{a.n}</Typography>
                      </Box>
                      <Box className={cls.pillsRow}>
                        <Typography variant="caption" component="span" className={cls.plateTintGreen}>{a.pl}</Typography>
                        <Typography variant="caption" component="span" className={cls.metaText}>Rm {a.rm}</Typography>
                        <Typography variant="caption" component="span" className={cls.secondaryText}>{a.typ}</Typography>
                        {a.note&&<Typography variant="caption" component="span" className={cls.noteText}>{a.note}</Typography>}
                      </Box>
                      {a.upsells&&a.upsells.length>0&&(
                        <Box className={cls.upsellsRow}>
                          {a.upsells.map((u,j)=>(
                            <Typography key={j} variant="caption" component="span" className={cls.upsellChip}>🛒 {u}</Typography>
                          ))}
                        </Box>
                      )}
                    </div>
                    <Typography variant="caption" component="span" className={cls.paidLabel} style={{color:a.paid?"#241C8A":P.nv}}>{a.paid?"Paid":"Unpaid"}</Typography>
                  </Box>
                ))}
              </Box>
            </Card>

            {/* Departing */}
            <Card title="Departing Today" subtitle={departingCount+" guests scheduled"}>
              <Box className={cls.listCol}>
                {DEPARTING.map((a,i)=>(
                  <Box key={i} className={cls.depRow} style={{border:"0.5px solid "+(a.st==="departed"?"var(--color-border-tertiary)":"var(--color-border-secondary)"),background:a.st==="departed"?"var(--color-background-secondary)":"var(--color-background-primary)",opacity:a.st==="departed"?.6:1}}>
                    <div>
                      <Box className={cls.etaLine}>
                        <Typography variant="body2" component="span" className={cls.coText} style={{color:a.st==="departed"?"var(--color-text-tertiary)":P.nv}}>{a.co}</Typography>
                        <Typography variant="body2" component="span" className={cls.nameInline}>{a.n}</Typography>
                      </Box>
                      <Box className={cls.pillsRow}>
                        <Typography variant="caption" component="span" className={cls.plateTintGray}>{a.pl}</Typography>
                        <Typography variant="caption" component="span" className={cls.metaText}>Bay {a.bay}</Typography>
                        <Typography variant="caption" component="span" className={cls.secondaryText}>{a.typ}</Typography>
                        {a.note&&<Typography variant="caption" component="span" className={cls.noteText}>{a.note}</Typography>}
                      </Box>
                      {a.upsells&&a.upsells.length>0&&(
                        <Box className={cls.upsellsRow}>
                          {a.upsells.map((u,j)=>(
                            <Typography key={j} variant="caption" component="span" className={cls.upsellChip}>🛒 {u}</Typography>
                          ))}
                        </Box>
                      )}
                    </div>
                    {stBadge(a.st)}
                  </Box>
                ))}
              </Box>
              <Box className={cls.freedBox}>
                <Box className={cls.freedRow}>
                  <Typography variant="caption" component="span" className={cls.freedLabel}>Bays freed up after departures</Typography>
                  <Typography variant="caption" component="span" className={cls.freedVal}>+{DEPARTING.filter(d=>d.st!=="departed").length} bays</Typography>
                </Box>
              </Box>
            </Card>

          </Box>
        </Box>
      )}

      {view==="valet" && (
        <Card title="Valet & Concierge Queue" subtitle="Live valet status">
          <Table className={cls.table}>
            <TableHead><TableRow>{["Guest","Room","Plate","Bay","Assigned To","Time","Status","Note"].map(h=><TableCell key={h} className={cls.th}>{h}</TableCell>)}</TableRow></TableHead>
            <TableBody>{VALET.map((v,i)=>(
              <TableRow key={i} className={cls.tr} style={{background:v.st==="retrieving"?"#E6F4E808":"transparent"}}>
                <TableCell className={cls.tdName}>{v.g}</TableCell>
                <TableCell className={cls.tdMono}>{v.rm}</TableCell>
                <TableCell className={cls.td}><Typography variant="caption" component="span" className={cls.plateTd}>{v.pl}</Typography></TableCell>
                <TableCell className={cls.tdSecondary}>{v.bay}</TableCell>
                <TableCell className={cls.td}><Box className={cls.assignedCell}><Typography variant="caption" component="div" className={cls.initialsDot}>{ini(v.by)}</Typography><Typography variant="body2" component="span">{v.by}</Typography></Box></TableCell>
                <TableCell className={cls.tdSecondary}>{v.time}</TableCell>
                <TableCell className={cls.td}>{stBadge(v.st)}</TableCell>
                <TableCell className={cls.tdTertiary}>{v.note}</TableCell>
              </TableRow>
            ))}</TableBody>
          </Table>
        </Card>
      )}
    </Box>
  );
}
